import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useNavigate, Link } from "react-router-dom";
import { GlassCard } from "@/components/common/GlassCard";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusPill } from "@/components/common/StatusPill";
import { LoadingState, EmptyState } from "@/components/common/States";
import { Users, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { POSITIONS, normalizePosition } from "@/lib/positionTraits";

export default function PlayersListPage() {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [positionFilter, setPositionFilter] = useState("all");
  const nav = useNavigate();

  useEffect(() => {
    api.get("/players").then((r) => setPlayers(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingState />;
  const filtered = players.filter((p) => {
    const matchesText = [p.full_name, p.position, p.school].join(" ").toLowerCase().includes(q.toLowerCase());
    const matchesPosition = positionFilter === "all" || normalizePosition(p.position) === positionFilter;
    return matchesText && matchesPosition;
  });

  return (
    <div className="max-w-[1500px] mx-auto">
      <PageHeader
        title="Players"
        subtitle="Roster view across your prospects."
        actions={
          <div className="flex items-center gap-2">
            <Select value={positionFilter} onValueChange={setPositionFilter}>
              <SelectTrigger data-testid="players-position-filter" className="w-[160px] bg-white/5 border-white/10 text-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="glass-strong border-white/10 max-h-72">
                <SelectItem value="all" data-testid="players-position-filter-option-all">All positions</SelectItem>
                {POSITIONS.map((p) => (
                  <SelectItem key={p} value={p} data-testid={`players-position-filter-option-${p}`}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              data-testid="players-search"
              placeholder="Search players…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-[240px] bg-white/5 border-white/10 text-1"
            />
          </div>
        }
      />
      {filtered.length === 0 ? (
        <EmptyState icon={Users} title="No players yet" description="Create your first player from the Admin Panel." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((p) => (
            <button
              key={p.id}
              data-testid={`player-card-${p.id}`}
              onClick={() => nav(`/app/players/${p.id}/profile`)}
              className="glass glass-hover p-5 text-left"
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-1 text-lg font-semibold">{p.full_name}</div>
                  <div className="text-2 text-xs">{p.school} · {p.class_year}</div>
                </div>
                <StatusPill tone="cyan">{p.position}</StatusPill>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2">
                <div className="glass p-2.5">
                  <div className="text-3 text-[10px] uppercase tracking-wider">Round</div>
                  <div className="text-1 text-sm font-medium">{p.projected_round || "—"}</div>
                </div>
                <div className="glass p-2.5">
                  <div className="text-3 text-[10px] uppercase tracking-wider">Confidence</div>
                  <div className="text-1 text-sm font-medium">{p.confidence || 0}%</div>
                </div>
              </div>
              <div className="mt-3 flex items-center justify-end text-cyan-300 text-xs">
                Open profile <ChevronRight className="h-3.5 w-3.5" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
